import { useRouteError } from 'react-router';
import { isDesktop } from '../platform';

const RELOAD_KEY = 'va-new-version-reload';

/**
 * True for the errors a page opened before an update gets when it asks for a script file the
 * update replaced (Chrome, Safari and Firefox word it differently).
 */
export function isStaleVersionError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /dynamically imported module|Importing a module script failed|not a valid JavaScript MIME type|Unable to preload CSS|Loading chunk|ChunkLoadError/i.test(msg);
}

/**
 * Reloads the page once to get the new version. Returns false when it already did a moment ago
 * (so a real problem shows an error instead of reloading forever).
 */
export function reloadForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked (private mode): at most one reload per page.
    const w = window as { __vaReloaded?: boolean };
    if (w.__vaReloaded) return false;
    w.__vaReloaded = true;
  }
  window.location.reload();
  return true;
}

/** Shown instead of the page when something breaks, instead of React Router's developer message. */
export function RouteError() {
  const error = useRouteError();
  const stale = isStaleVersionError(error);
  if (stale && reloadForNewVersion()) {
    return (
      <div className="container">
        <div className="empty">
          <h3>Updating…</h3>
          <p>The store was just updated. Loading the new version.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="container">
      <div className="empty">
        <h3>Something went wrong</h3>
        <p>{stale ? 'The store was just updated. Reload the page to get the new version.' : 'Reload the page to try again. If it keeps happening, write to support@va-plugin.com.'}</p>
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Reload</button>
          <a className="btn btn-outline" href={isDesktop ? '#/' : '/'}>Back to the store</a>
        </div>
      </div>
    </div>
  );
}
